import React, { useState } from 'react';
import { Upload, Flex, Alert, Progress, Typography, Button, Spin } from 'antd';
import { InboxOutlined, VideoCameraOutlined, RightOutlined, ReloadOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import LspivService from '../../../services/LspivService';
import FrameCanvas from './FrameCanvas';

const { Dragger } = Upload;
const { Title, Text } = Typography;

const VideoStep = ({ jobId, job, onRefresh, onNext }) => {
    const { t } = useTranslation();
    const [uploading, setUploading] = useState(false);
    const [progress, setProgress] = useState(0);
    const [error, setError] = useState(null);
    const [replacing, setReplacing] = useState(false);

    const status = job?.status || {};
    const extracting = status.status === 'running' && status.step === 'extract-frames';
    const extractFailed = status.status === 'error' && status.step === 'extract-frames';
    const ready = !extracting && !extractFailed && job?.frames_count > 0 && status.first_frame;

    const handleUpload = async (file) => {
        setUploading(true);
        setError(null);
        setProgress(0);
        try {
            await LspivService.uploadVideo(jobId, file, setProgress);
            setReplacing(false);
            await onRefresh();
        } catch (err) {
            setError(err?.response?.data?.detail || t('lspiv.video.uploadError'));
        } finally {
            setUploading(false);
        }
        return false; // загрузку делаем сами, не antd
    };

    const showDragger = uploading || replacing || (!extracting && !ready);

    return (
        <Flex vertical gap={20}>
            <Title level={4} style={{ margin: 0 }}>
                <VideoCameraOutlined /> {t('lspiv.video.title')}
            </Title>
            <Alert type="info" showIcon message={t('lspiv.video.tipsTitle')} description={t('lspiv.video.tips')} />

            {error && <Alert type="error" message={error} showIcon />}
            {extractFailed && !uploading && (
                <Alert type="error" showIcon message={t('lspiv.video.extractError')} description={status.error} />
            )}

            {showDragger && (
                <Dragger
                    beforeUpload={handleUpload}
                    showUploadList={false}
                    accept="video/*"
                    disabled={uploading}
                    style={{ borderRadius: 8 }}
                >
                    <p className="ant-upload-drag-icon"><InboxOutlined /></p>
                    <p className="ant-upload-text">{t('lspiv.video.draggerText')}</p>
                    <p className="ant-upload-hint">{t('lspiv.video.draggerHint')}</p>
                    {uploading && (
                        <div style={{ maxWidth: 400, margin: '12px auto' }}>
                            <Progress percent={progress} />
                            <Text type="secondary">{t('lspiv.video.uploading')}</Text>
                        </div>
                    )}
                </Dragger>
            )}

            {extracting && !uploading && (
                <Flex align="center" gap={12} style={{ padding: '24px 0' }}>
                    <Spin />
                    <Text>{t('lspiv.video.extracting')}</Text>
                </Flex>
            )}

            {ready && !replacing && !uploading && (
                <>
                    <div style={{ maxWidth: 720 }}>
                        <FrameCanvas src={LspivService.frameUrl(jobId, status.first_frame)} />
                    </div>
                    <Text type="secondary">
                        {t('lspiv.video.info', {
                            duration: (status.video_duration || 0).toFixed(1),
                            frames: job.frames_count,
                            fps: Math.round(status.fps / status.every),
                            width: status.frame_width,
                            height: status.frame_height,
                        })}
                    </Text>
                    <Flex gap={12} wrap="wrap">
                        <Button type="primary" onClick={onNext}>
                            {t('lspiv.video.next')} <RightOutlined />
                        </Button>
                        <Button icon={<ReloadOutlined />} onClick={() => setReplacing(true)}>
                            {t('lspiv.video.replace')}
                        </Button>
                    </Flex>
                </>
            )}
        </Flex>
    );
};

export default VideoStep;
